import momentsData from "../moments.json";
import type { KeepType } from "./dto";

/** A "moment" of a gathering — a Keep's top-level organizational unit. */
export interface MomentDef {
  /** Stable slug, e.g. "first-dance". Used as the public id everywhere. */
  key: string;
  /** The Keep type this moment belongs to, or "any" for universal buckets (uncategorized/videos). */
  type: KeepType | "any";
  /** Display name, e.g. "First Dance". */
  label: string;
  /** One-line caption shown under the title. */
  desc: string;
  /** CLIP zero-shot prompt used by the AI worker to classify photos into this moment. */
  prompt: string;
  /**
   * Optional ensemble of paraphrased prompts. When present the AI worker averages their
   * CLIP text embeddings (more robust zero-shot than a single `prompt`). Worker-only —
   * the TS side never reads it, but it lives here so the two moments.json copies stay
   * byte-identical.
   */
  prompts?: string[];
  /** Keyword list powering the natural-language search keyword-fusion arm. */
  keywords: string[];
  /** Two-stop watercolor wash used for placeholder covers in the UI. */
  wash: [string, string];
  /** Order the moments appear in the album (chronological-ish). */
  sortOrder: number;
}

export const MOMENTS: MomentDef[] = momentsData as MomentDef[];

export const MOMENT_KEYS = MOMENTS.map((m) => m.key);

export function momentByKey(key: string): MomentDef | undefined {
  return MOMENTS.find((m) => m.key === key);
}

/** The gathering types a Keep can be, with display copy. `default: true` marks the default. */
export interface KeepTypeDef {
  id: KeepType;
  label: string;
  /** Short noun used in guest copy, e.g. "wedding" → "the wedding". */
  noun: string;
  /** Small hero kicker shown above the name in the guest app, e.g. "The Wedding Of". */
  kicker: string;
}

export const KEEP_TYPES: KeepTypeDef[] = [
  { id: "wedding", label: "Wedding", noun: "wedding", kicker: "The Wedding Of" },
  { id: "birthday", label: "Birthday", noun: "birthday", kicker: "Celebrating" },
  { id: "baby-shower", label: "Baby shower", noun: "baby shower", kicker: "A Shower For" },
  { id: "graduation", label: "Graduation", noun: "graduation", kicker: "Congratulations" },
  { id: "holiday", label: "Holiday", noun: "holiday", kicker: "Gathered For" },
  { id: "reunion", label: "Reunion", noun: "reunion", kicker: "Reunited" },
  { id: "party", label: "Party", noun: "party", kicker: "Celebrating" },
  { id: "other", label: "Other gathering", noun: "gathering", kicker: "Gathered For" },
];

export const DEFAULT_KEEP_TYPE: KeepType = "wedding";

export function keepTypeDef(type: string | null | undefined): KeepTypeDef {
  return KEEP_TYPES.find((t) => t.id === type) ?? KEEP_TYPES[0]!;
}

/** The moments for a given Keep type, in display order, plus the universal buckets
 * (uncategorized/videos). This is the set a Keep of `type` is organized into. */
export function momentsForType(type: string | null | undefined): MomentDef[] {
  const t = keepTypeDef(type).id;
  return MOMENTS.filter((m) => m.type === t || m.type === "any").sort((a, b) => a.sortOrder - b.sortOrder);
}

/** CLIP-classifiable moment keys for a Keep type (excludes the empty-prompt universal buckets). */
export function classifiableMomentKeysForType(type: string | null | undefined): string[] {
  return momentsForType(type).filter((m) => m.prompt).map((m) => m.key);
}

/** Suggested natural-language search chips, by Keep type. */
const SUGGESTED_BY_TYPE: Record<KeepType, string[]> = {
  wedding: ["Show me the first dance", "Show me the vows", "Show me cake cutting", "Show me the speeches", "Show me dancing", "Show me sunset pictures"],
  birthday: ["Show me the cake", "Show me the candles", "Show me presents", "Show me dancing", "Show me the games", "Show me group photos"],
  "baby-shower": ["Show me the mom-to-be", "Show me the cake", "Show me gifts", "Show me the games", "Show me group photos"],
  graduation: ["Show me the cap toss", "Show me the stage", "Show me family photos", "Show me the party", "Show me candids"],
  holiday: ["Show me the meal", "Show me gifts", "Show me the group photo", "Show me candids", "Show me the tree"],
  reunion: ["Show me the group photo", "Show me the meal", "Show me the activities", "Show me candids", "Show me arrivals"],
  party: ["Show me the big moment", "Show me dancing", "Show me food and drinks", "Show me group photos", "Show me candids"],
  other: ["Show me the highlights", "Show me group photos", "Show me candids", "Show me people"],
};

export function suggestedSearchesForType(type: string | null | undefined): string[] {
  return SUGGESTED_BY_TYPE[keepTypeDef(type).id];
}

/** Back-compat default (wedding) suggestions. */
export const SUGGESTED_SEARCHES = SUGGESTED_BY_TYPE.wedding;
